import React, { useState, useMemo, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { FormikProps, useFormikContext } from 'formik';
import isEqual from 'lodash/isEqual';

import { usePrefUnitForMeasurementClass } from 'hooks';
import {
  DeviceSelectionList,
  HeightWeightBanner,
  HeightWeightForm,
  SuggestionBanner
} from 'pages/Dashboard/Common/Devices/List';
import { IBodyMeasurementFields } from 'utils/body-measurements';
import {
  calcRecommendedArmCircumference,
  canRecommendCuffFor,
  selectCuff,
  getDefaultCuff
} from 'utils/RecommendCuffSku';

import { IInvitation } from '../Onboard';
import AccessoriesList from './AccessoriesList';

/** The cap the box has always advertised through `max`, now enforced rather than suggested. */
export const MAX_BULK_QUANTITY = 50;
const clampQuantity = (value: number) => Math.min(MAX_BULK_QUANTITY, Math.max(1, value));

const isHeightMissing = (height?: string): boolean => !(parseFloat(height ?? '') > 0);

const isWeightMissing = (weight?: string): boolean => !(parseFloat(weight ?? '') > 0);

interface IDevicesProps {
  devices?: IDeviceOrderingCatalogItem[];
  suggestedDevices: IDeviceOrderingCatalogItem[];
  patientDevices: IDeviceListItem[];
  bulkOrderDevices: boolean;
  bulkOrderAccessories?: boolean;
  heightWeightMode?: 'inline' | 'medicalStep';
  heightWeightLoading?: boolean;
  bodyMeasurePrefill?: IBodyMeasurementFields | null;
  prefillAppliedRef?: React.MutableRefObject<boolean>;
  onSaveHeightWeight?: (entered: IBodyMeasurementFields) => Promise<void>;
  onGoToMedicalInfo?: (formik: FormikProps<IInvitation>) => void;
}

const recommendArmCircumference = (
  medicalInfo: IInvitation['medicalInfo'],
  patientInfo: IInvitation['patientInfo'],
  heightUnit: string | undefined,
  weightUnit: string | undefined
) =>
  calcRecommendedArmCircumference({
    heightRaw: medicalInfo.height,
    heightInches: medicalInfo.heightInches,
    heightUnit: heightUnit ?? 'm',
    weightRaw: medicalInfo.weight,
    weightUnit: weightUnit ?? 'kg',
    gender: patientInfo.gender,
    bday: patientInfo.bday
  });

const getDefaultCuffSku = (options: IDeviceOrderingOption[] | undefined): string | undefined =>
  getDefaultCuff(options ?? [])?.sku;

const getCuffKeyForDevice = (
  device: { options?: IDeviceOrderingOption[] },
  deviceEntries: Record<string, number>
): string | undefined =>
  device.options?.map((o) => o.sku).find((sku) => deviceEntries[sku] !== undefined);

const Devices = ({
  devices = [],
  patientDevices,
  bulkOrderDevices,
  bulkOrderAccessories,
  suggestedDevices,
  heightWeightMode,
  heightWeightLoading,
  bodyMeasurePrefill,
  prefillAppliedRef,
  onSaveHeightWeight,
  onGoToMedicalInfo
}: IDevicesProps) => {
  const intl = useIntl();
  const formik = useFormikContext<IInvitation>();
  const { values, setFieldValue } = formik;
  const heightUnit = usePrefUnitForMeasurementClass('height');
  const weightUnit = usePrefUnitForMeasurementClass('weight');
  const hasDevices = devices.length > 0;
  const [showSuggestionBanner, setShowSuggestionBanner] = useState(true);
  const [showHeightWeightForm, setShowHeightWeightForm] = useState(false);
  /**
   * What is being typed into each quantity box, before it is a number. Writing straight to Formik
   * meant an empty field could not exist: clearing the box parsed to NaN, fell back to 1, and put
   * the 1 back under the caret, so "50" could only be reached with the spinner (QA v26.10, #13).
   */
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});
  const alreadyAssignedSkus = patientDevices
    .filter((item) => !item.deviceOrdering?.parent)
    .map((item) => item.deviceOrdering?.sku)
    .filter((sku): sku is string => Boolean(sku));

  const selectedSkus = [...alreadyAssignedSkus, ...Object.keys(values.devices)];

  const selectionsOptions = useMemo(() => {
    const result: Record<string, string> = {};
    devices.forEach((d) => {
      const cuffKey = getCuffKeyForDevice(d, values.devices);
      if (cuffKey !== undefined) result[d.sku] = cuffKey;
    });
    return result;
  }, [devices, values.devices]);

  const optionRecommendations = useMemo(() => {
    const acCm = recommendArmCircumference(
      values.medicalInfo,
      values.patientInfo,
      heightUnit,
      weightUnit
    );
    if (!acCm) return {};
    return devices.reduce<Record<string, string>>((acc, d) => {
      const optionSku = selectCuff(acCm, d.options ?? []);
      if (optionSku) acc[d.sku] = optionSku;
      return acc;
    }, {});
  }, [values.medicalInfo, values.patientInfo, heightUnit, weightUnit, devices]);

  const heightWeightMissing =
    isHeightMissing(values.medicalInfo.height) || isWeightMissing(values.medicalInfo.weight);
  const needsHeightWeight =
    !!heightWeightMode &&
    !heightWeightLoading &&
    heightWeightMissing &&
    canRecommendCuffFor(values.patientInfo);

  useEffect(() => {
    if (!bodyMeasurePrefill || !prefillAppliedRef || prefillAppliedRef.current) return;
    prefillAppliedRef.current = true;
    const { height, heightInches, weight } = bodyMeasurePrefill;
    if (height !== undefined) setFieldValue('medicalInfo.height', height);
    if (heightInches !== undefined) setFieldValue('medicalInfo.heightInches', heightInches);
    if (weight !== undefined) setFieldValue('medicalInfo.weight', weight);
  }, [bodyMeasurePrefill, prefillAppliedRef, setFieldValue]);

  useEffect(() => {
    const bmiChanged = values.bmiChangedFlag;
    const newDevices = { ...values.devices };

    devices.forEach((d) => {
      if (!d.options?.length) return;
      const parentQty = values.devices[d.sku];
      if (parentQty === undefined) return;

      const recommendedSku = optionRecommendations[d.sku] ?? getDefaultCuffSku(d.options);
      if (!recommendedSku) return;

      const currentCuffKey = getCuffKeyForDevice(d, values.devices);
      if (currentCuffKey === undefined) {
        newDevices[recommendedSku] = parentQty;
      } else if (bmiChanged && currentCuffKey !== recommendedSku) {
        newDevices[recommendedSku] = newDevices[currentCuffKey];
        delete newDevices[currentCuffKey];
      }
    });

    if (!isEqual(newDevices, values.devices)) setFieldValue('devices', newDevices);
    if (bmiChanged) setFieldValue('bmiChangedFlag', false);
  }, [optionRecommendations, values.devices, devices, setFieldValue, values.bmiChangedFlag]);

  const handleSaveHeightWeight = async (entered: IBodyMeasurementFields) => {
    await onSaveHeightWeight?.(entered);
    const medicalInfo = { ...values.medicalInfo, ...entered };
    const canRecommend = !!recommendArmCircumference(
      medicalInfo,
      values.patientInfo,
      heightUnit,
      weightUnit
    );
    // One field at a time: `values` was captured before the save and may be stale.
    setFieldValue('medicalInfo.height', entered.height);
    setFieldValue('medicalInfo.heightInches', entered.heightInches);
    setFieldValue('medicalInfo.weight', entered.weight);
    setFieldValue('bmiChangedFlag', canRecommend);
    setShowHeightWeightForm(false);
  };

  const handleAddHeightWeight = () => {
    if (heightWeightMode === 'inline') setShowHeightWeightForm(true);
    else onGoToMedicalInfo?.(formik);
  };

  const renderCuffNotice = () => {
    if (!needsHeightWeight) return null;
    if (showHeightWeightForm && heightWeightMode === 'inline')
      return (
        <HeightWeightForm
          onSave={handleSaveHeightWeight}
          onCancel={() => setShowHeightWeightForm(false)}
        />
      );
    return <HeightWeightBanner onAdd={handleAddHeightWeight} />;
  };

  const handleToggle = (sku: string) => {
    if (alreadyAssignedSkus.includes(sku)) return;
    const device = devices.find((d) => d.sku === sku);
    if (!device) return;

    const newDevices = { ...values.devices };
    if (newDevices[sku] !== undefined) {
      delete newDevices[sku];
      device.options?.forEach((o) => delete newDevices[o.sku]);
    } else {
      newDevices[sku] = 1;
    }
    setFieldValue('devices', newDevices);
  };

  const handleAcceptSuggested = (suggested: IDeviceOrderingCatalogItem[]) => {
    const newDevices: Record<string, number> = {};
    suggested.forEach((d) => {
      if (!alreadyAssignedSkus.includes(d.sku)) {
        newDevices[d.sku] = 1;
      }
    });
    setFieldValue('devices', newDevices);
    setShowSuggestionBanner(false);
  };

  // Device prices are deliberately absent from this screen (Alex, round 3): the clinician
  // ordering a device is not the person paying for it, and the balance a prepaid org has left is
  // shown on Billing & Credits. The rate card still governs what the order costs.
  const commitQuantity = (
    device: { sku: string; options?: IDeviceOrderingOption[] },
    quantity: number
  ) => {
    const newDevices = { ...values.devices, [device.sku]: quantity };
    device.options?.forEach((o) => {
      if (newDevices[o.sku] !== undefined) newDevices[o.sku] = quantity;
    });
    setFieldValue('devices', newDevices);
  };

  const renderQuantityFooter = (device: { sku: string; options?: IDeviceOrderingOption[] }) => {
    if (!bulkOrderDevices) return null;
    const qty = values.devices[device.sku];
    return (
      <>
        <div
          className="mt-2 d-flex align-items-center gap-2"
          style={{ visibility: qty === undefined ? 'hidden' : 'visible' }}
          onClick={(e) => e.stopPropagation()}
        >
          <label className="small mb-0 text-secondary">
            {intl.formatMessage({ id: 'qtyKey' })}:
          </label>
          <input
            type="number"
            min={1}
            max={MAX_BULK_QUANTITY}
            className="form-control form-control-sm"
            style={{ width: 70 }}
            value={quantityDrafts[device.sku] ?? String(qty ?? 1)}
            onChange={(e) => {
              if (qty === undefined) return;
              setQuantityDrafts((drafts) => ({ ...drafts, [device.sku]: e.target.value }));
              const parsed = parseInt(e.target.value, 10);
              if (Number.isNaN(parsed)) return;
              commitQuantity(device, clampQuantity(parsed));
            }}
            onBlur={() => {
              // Leaving the box empty, at zero, or above the cap is a half-finished edit, not an
              // order: settle it here rather than fighting the caret on every keystroke.
              const parsed = parseInt(quantityDrafts[device.sku] ?? '', 10);
              const settled = Number.isNaN(parsed) ? qty ?? 1 : clampQuantity(parsed);
              setQuantityDrafts((drafts) => {
                const next = { ...drafts };
                delete next[device.sku];
                return next;
              });
              if (qty !== undefined) commitQuantity(device, settled);
            }}
          />
        </div>
      </>
    );
  };

  if (bulkOrderAccessories) {
    return (
      <>
        <h4 className="mb-4">{intl.formatMessage({ id: 'accessoriesKey' })}</h4>
        <AccessoriesList accessories={devices} />
      </>
    );
  }

  return (
    <>
      <h4 className="mb-4" data-test="select-devices-title">
        {intl.formatMessage({ id: 'medicalDevicesKey' })}
      </h4>

      {devices && !hasDevices && (
        <p data-test="no-devices">{intl.formatMessage({ id: 'groupNoDevicesKey' })}</p>
      )}

      {devices && hasDevices && (
        <>
          {showSuggestionBanner && (
            <SuggestionBanner
              suggestedDevices={suggestedDevices}
              onAccept={() => handleAcceptSuggested(suggestedDevices)}
              onDismiss={() => setShowSuggestionBanner(false)}
            />
          )}
          <DeviceSelectionList
            devices={devices}
            selectedSkus={selectedSkus}
            orderedSkus={Object.keys(values.devices)}
            onToggle={handleToggle}
            maxSelectionsPerGroup={bulkOrderDevices ? undefined : 1}
            renderCardFooter={renderQuantityFooter}
            selectionsOptions={selectionsOptions}
            onOptionSelect={(sku, optionSku) => {
              const newDevices = { ...values.devices };
              const qty = newDevices[sku] ?? 1;
              const previousOptionSku = selectionsOptions[sku];
              if (previousOptionSku) delete newDevices[previousOptionSku];
              newDevices[optionSku] = qty;
              setFieldValue('devices', newDevices);
            }}
            recommendation={optionRecommendations}
            patientDevices={patientDevices}
            renderCuffNotice={renderCuffNotice}
          />
        </>
      )}
    </>
  );
};

export default Devices;
